// types
import { Contract, Entity, V2Contract, WorkPlan } from '../../../../@types';

/**
 * Helper utilities for accessing V2 contract data
 * V2 contracts now return amounts[] and entities[] arrays from the v_contract view
 *
 * IMPORTANT: The work plan's currency is the source of truth for all contract amounts.
 * When aggregating or displaying contract amounts, always use the work plan's currency.
 */

// V1 Contract Helpers (for accessing raw Contract objects)
// Used when working with legacy contracts from /contracts endpoint

export const getV1ContractAmount = (contract: Contract): number => {
	if (contract.amounts && contract.amounts.length > 0) {
		return contract.amounts[0].amount || 0;
	}
	return 0;
};

export const getV1ContractCurrency = (contract: Contract): string => {
	if (contract.amounts && contract.amounts.length > 0) {
		return contract.amounts[0].currency || 'USD';
	}
	return 'USD';
};

/**
 * Get the contract amount for a specific currency.
 * If the currency is not found, returns the first amount with its currency.
 */
export const getV1ContractAmountForCurrency = (
	contract: Contract,
	currency: string
): { amount: number; currency: string } => {
	if (!contract.amounts || contract.amounts.length === 0) {
		return { amount: 0, currency };
	}

	// Try to find amount matching the requested currency
	const matchingAmount = contract.amounts.find((a) => a.currency === currency);
	if (matchingAmount) {
		return { amount: matchingAmount.amount || 0, currency: matchingAmount.currency };
	}

	// Fall back to first amount if no match
	const firstAmount = contract.amounts[0];
	return { amount: firstAmount.amount || 0, currency: firstAmount.currency || 'USD' };
};

export const getV1ContractSupplier = (contract: Contract): number | Entity | null => {
	if (contract.entities && contract.entities.length > 0) {
		return contract.entities[0];
	}
	return null;
};

export const getV1ContractSupplierName = (contract: Contract): string => {
	const supplier = getV1ContractSupplier(contract);
	if (typeof supplier === 'object' && supplier !== null) {
		return supplier.name || 'Unknown Supplier';
	}
	if (typeof supplier === 'number') {
		return `Supplier #${supplier}`;
	}
	return 'Unknown Supplier';
};

// Contract Helpers (for V2ContractSanitized objects)
// These access arrays from the v_contract view

export interface ContractAmountByCurrency {
	currency: string;
	amount: number;
}

/**
 * Get contract amounts grouped by currency
 * Returns an array of { currency, amount } objects
 */
export const getContractAmountsByCurrency = (contract: V2Contract): ContractAmountByCurrency[] => {
	if (!contract.amounts || contract.amounts.length === 0) return [];

	const byCurrency = contract.amounts.reduce(
		(acc, a) => {
			const currency = a.currency || 'USD';
			acc[currency] = (acc[currency] || 0) + (a.amount || 0);
			return acc;
		},
		{} as Record<string, number>
	);

	return Object.entries(byCurrency).map(([currency, amount]) => ({ currency, amount }));
};

/**
 * Get the total contract amount in a specific currency.
 *
 * Returns the sum of every contract amount denominated in the requested
 * currency, or `null` when the contract carries no amount in that currency.
 *
 * The nullable return is deliberate: it forces callers to deal explicitly
 * with the "currency mismatch" case rather than silently substituting a
 * number from another currency. Pairing a number with a label of a
 * different currency leads to authorisation errors (e.g. an ETB number
 * rendered under a USD label), so the helper refuses to lie.
 */
export const getContractAmountForCurrency = (
	contract: V2Contract,
	currency: string
): number | null => {
	if (!contract.amounts || contract.amounts.length === 0) return null;

	const matchingAmounts = contract.amounts.filter((a) => (a.currency || 'USD') === currency);
	if (matchingAmounts.length === 0) return null;

	return matchingAmounts.reduce((sum, a) => sum + (a.amount || 0), 0);
};

/**
 * Get total contract amount in the work plan's currency.
 * Returns `null` when the contract carries no amount in that currency
 * (see `getContractAmountForCurrency` for the rationale).
 */
export const getContractAmountInWorkPlanCurrency = (
	contract: V2Contract,
	workPlan: WorkPlan | { currency: string }
): number | null => {
	return getContractAmountForCurrency(contract, workPlan.currency);
};

/**
 * @deprecated Returns the first currency's amount with no indication of
 * which currency it is. Prefer `getContractAmountsByCurrency` (returns
 * `{ currency, amount }[]`) or `getContractAmountForCurrency` (nullable
 * lookup by currency) so the value is always paired with its label.
 */
export const getContractAmount = (contract: V2Contract): number => {
	const amounts = getContractAmountsByCurrency(contract);
	if (amounts.length === 0) return 0;
	// Return the first currency's amount only
	return amounts[0].amount;
};

/**
 * @deprecated Returns the contract's first amount currency, which is not
 * necessarily the currency a given caller cares about (work plan, voucher,
 * etc. may differ). Prefer `getContractAmountsByCurrency` and pick the
 * currency explicitly at the call site.
 */
export const getContractCurrency = (contract: V2Contract): string => {
	if (!contract.amounts || contract.amounts.length === 0) return 'USD';
	return contract.amounts[0].currency || 'USD';
};

/**
 * Check if contract has multiple currencies
 */
export const hasMultipleCurrencies = (contract: V2Contract): boolean => {
	return getContractAmountsByCurrency(contract).length > 1;
};

/**
 * Get the contract number
 */
export const getContractNum = (contract: V2Contract): string => {
	return contract.num || '';
};

/**
 * Get the first supplier entity name
 */
export const getContractSupplierName = (contract: V2Contract): string => {
	const supplier = contract.entities?.find((e) => e.type === 'SUPPLIER');
	return supplier?.name || 'Unknown Supplier';
};

/**
 * Get all supplier entities
 */
export const getContractSuppliers = (contract: V2Contract) => {
	return contract.entities?.filter((e) => e.type === 'SUPPLIER') || [];
};

/**
 * Get total amount paid
 * Note: This assumes totalPaid is in the primary currency
 */
export const getContractAmountPaid = (contract: V2Contract): number => {
	return contract.totalPaid || 0;
};

/**
 * Get total amount paid, restated into the work plan (reporting) currency.
 * Prefer this on reporting-currency surfaces (activity / work-plan / contracts
 * table / operations dashboard) where `totalPaid` (contract currency) would be
 * rendered under the wrong currency label for cross-currency contracts.
 */
export const getContractAmountPaidReporting = (contract: V2Contract): number => {
	return contract.totalPaidReporting || 0;
};

/**
 * Get the amount paid against this contract under V1, before it was onboarded
 * into V2. V1 and V2 share the same contract record, so a contract may already
 * carry legacy disbursements. The value is denominated in the contract's
 * currency by the server (matching `totalPaid`).
 */
export const getContractAmountPaidV1 = (contract: V2Contract): number => {
	return contract.totalPaidV1 || 0;
};

/**
 * Get the contract's commitment amount in the work plan's currency.
 *
 * If `commitmentAmount` is explicitly set on the contract, that value is
 * returned as-is (it is denominated in the work plan's currency by
 * convention — the column is a scalar with no currency tag).
 *
 * Otherwise the helper falls back to the full contract amount in the
 * work plan's currency. Returns `null` when no amount in that currency
 * exists, so callers must decide explicitly how to render a missing
 * cross-currency value rather than silently coercing.
 */
export const getContractCommitmentAmount = (
	contract: V2Contract,
	workPlan: WorkPlan | { currency: string } | null | undefined
): number | null => {
	if (contract.commitmentAmount != null) {
		return contract.commitmentAmount;
	}
	const currency = workPlan?.currency || 'USD';
	return getContractAmountForCurrency(contract, currency);
};

/**
 * Get amount remaining (total - paid) for the primary currency
 * @deprecated Use getContractAmountsByCurrency for multi-currency contracts
 */
export const getContractAmountRemaining = (contract: V2Contract): number => {
	const total = getContractAmount(contract);
	return total - (contract.totalPaid || 0) - (contract.totalPaidV1 || 0);
};

/**
 * Get payment progress percentage for the primary currency
 * @deprecated Use getContractAmountsByCurrency for multi-currency contracts
 */
export const getContractPaymentProgressPct = (contract: V2Contract): number => {
	const total = getContractAmount(contract);
	if (total <= 0) return 0;
	return Math.round((((contract.totalPaid || 0) + (contract.totalPaidV1 || 0)) / total) * 100);
};

/**
 * Get the activity name
 */
export const getContractActivityName = (contract: V2Contract): string => {
	return contract.activityName || 'Unknown Activity';
};

/**
 * Get the work plan name
 */
export const getContractWorkPlanName = (contract: V2Contract): string => {
	return contract.workPlanName || 'Unknown Work Plan';
};

/**
 * Get milestones completed count
 */
export const getContractMilestonesCompleted = (contract: V2Contract): number => {
	return contract.milestonesCompleted || 0;
};

/**
 * Get total milestones count
 */
export const getContractTotalMilestones = (contract: V2Contract): number => {
	return contract.totalPaymentMilestones || 0;
};
